import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Alert,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { db } from '../firebase';
import { collection, getDocs, query } from 'firebase/firestore';

const CustomerTiersDialog = ({ open, onClose, onSave, initialData = null }) => {
  const [email, setEmail] = useState('');
  const [tiers, setTiers] = useState([
    { maxAmount: 100, fee: 7.5, type: 'fixed' },
    { maxAmount: 1000, fee: 5, type: 'percentage' },
    { maxAmount: null, fee: 4, type: 'percentage' }
  ]);
  const [minFee, setMinFee] = useState(0);
  const [note, setNote] = useState('');
  const [testAmount, setTestAmount] = useState(100);
  const [customerEmails, setCustomerEmails] = useState([]);

  useEffect(() => {
    if (initialData) {
      setEmail(initialData.email || '');
      if (initialData.type === 'custom_tiers' && initialData.tiers) {
        setTiers(initialData.tiers);
        setMinFee(initialData.minFee !== undefined ? initialData.minFee : 0);
      }
      setNote(initialData.note || '');
    }
  }, [initialData]);

  // Fetch customer emails when dialog opens
  useEffect(() => {
    const fetchCustomerEmails = async () => {
      if (open && !initialData) {
        try {
          // Try to get from users collection
          const usersRef = collection(db, 'users');
          const usersSnapshot = await getDocs(query(usersRef));
          const emails = usersSnapshot.docs.map(doc => doc.data().email).filter(email => email);

          // Remove duplicates and sort
          const uniqueEmails = [...new Set(emails)].sort();
          setCustomerEmails(uniqueEmails);
        } catch (error) {
          setCustomerEmails([]);
        }
      }
    };

    fetchCustomerEmails();
  }, [open, initialData]);

  const handleAddTier = () => {
    setTiers([...tiers, { maxAmount: null, fee: 3, type: 'percentage' }]);
  };

  const handleRemoveTier = (index) => {
    if (tiers.length > 1) {
      setTiers(tiers.filter((_, i) => i !== index));
    }
  };

  const handleTierChange = (index, field, value) => {
    const newTiers = [...tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setTiers(newTiers);
  };

  const calculateTestFee = () => {
    const amount = parseFloat(testAmount);
    if (!amount || amount <= 0) return null;

    // Find applicable tier
    const tier = tiers.find(t => !t.maxAmount || amount <= t.maxAmount);
    if (!tier) return null;

    let feeAmount;
    if (tier.type === 'fixed') {
      feeAmount = tier.fee;
    } else {
      feeAmount = amount * (tier.fee / 100);
    }

    // Apply minimum fee
    feeAmount = Math.max(feeAmount, minFee);

    return {
      amount: amount,
      fee: feeAmount.toFixed(2),
      percentage: ((feeAmount / amount) * 100).toFixed(2),
      total: (amount + feeAmount).toFixed(2)
    };
  };

  const handleSave = () => {
    const override = {
      email,
      type: 'custom_tiers',
      tiers,
      minFee,
      note
    };
    onSave(override);
    onClose();
  };

  const testFee = calculateTestFee();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          {initialData ? 'Edit' : 'Add'} Custom Fee Tiers
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
          {/* Customer Email */}
          {initialData?.email ? (
            <TextField
              fullWidth
              label="Customer Email"
              value={email}
              disabled
              helperText="Email cannot be changed when editing"
            />
          ) : (
            <Autocomplete
              freeSolo
              options={customerEmails}
              value={email}
              onChange={(event, newValue) => {
                setEmail(newValue || '');
              }}
              onInputChange={(event, newInputValue) => {
                setEmail(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer Email"
                  helperText="Select from existing customers or enter a new email"
                  fullWidth
                />
              )}
            />
          )}

          {/* Minimum Fee */}
          <TextField
            label="Minimum Fee"
            type="number"
            value={minFee}
            onChange={(e) => setMinFee(parseFloat(e.target.value) || 0)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
            helperText="Minimum fee regardless of percentage"
          />

          {/* Tiers Table */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Fee Tiers
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Amount Range</TableCell>
                    <TableCell>Fee Type</TableCell>
                    <TableCell>Fee Value</TableCell>
                    <TableCell width={50}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tiers.map((tier, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          type="number"
                          value={tier.maxAmount || ''}
                          onChange={(e) => handleTierChange(index, 'maxAmount', e.target.value ? parseFloat(e.target.value) : null)}
                          placeholder="Unlimited"
                          size="small"
                          fullWidth
                          InputProps={{
                            startAdornment: <InputAdornment position="start">Up to $</InputAdornment>
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={tier.type}
                          onChange={(e) => handleTierChange(index, 'type', e.target.value)}
                          size="small"
                          fullWidth
                        >
                          <MenuItem value="fixed">Fixed</MenuItem>
                          <MenuItem value="percentage">Percentage</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={tier.fee}
                          onChange={(e) => handleTierChange(index, 'fee', parseFloat(e.target.value) || 0)}
                          size="small"
                          fullWidth
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {tier.type === 'fixed' ? '$' : '%'}
                              </InputAdornment>
                            )
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveTier(index)}
                          disabled={tiers.length === 1}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddTier}
              size="small"
              sx={{ mt: 1 }}
            >
              Add Tier
            </Button>
          </Box>

          {/* Test Calculator */}
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Test Calculator
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Test Amount"
                type="number"
                value={testAmount}
                onChange={(e) => setTestAmount(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
              {testFee && (
                <Box>
                  <Typography variant="body2">
                    Fee: ${testFee.fee} ({testFee.percentage}%)
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Total: ${testFee.total}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Note */}
          <TextField
            fullWidth
            label="Note (Optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            rows={2}
            helperText="Add a note about this fee structure"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          startIcon={<SaveIcon />}
          disabled={!email}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerTiersDialog;
